import React, { useState } from 'react';
import BaseModal from '../../../components/Portal/BaseModal';
import CourseGroupFormModal from '../CourseGroupForm/CourseGroupFormModal';
import {
  ModalHeader, CloseButton, ModalBody, ModalFooter,
  Field, FieldRow, PrimaryButton, SecondaryButton, ErrorState
} from './Timetable.styles';
import {
  LESSON_TYPE_OPTIONS, LESSON_STATUS_OPTIONS, addOneOffLesson
} from '../../../api/timetableApi';

/**
 * Add a class to the timetable.
 *
 * Two different things wear the same button, and the lesson type is the question that tells
 * them apart:
 *
 * - A trial, an assessment, a makeup or an ad-hoc class is **one lesson**. It belongs to no
 *   course group on purpose — modelling a trial as a term-long course group would clutter the
 *   course list and start the credit machinery. `guestName` covers the common case of a
 *   prospective student with no record yet. That is the form below.
 * - A **regular** class is not one lesson at all; it is a term of them. So choosing Regular
 *   hands over to the course group form — the same one the Course Groups page opens — seeded
 *   with the day, time, room and campus of the cell that was clicked. The point is that a
 *   class can be set up without leaving the timetable.
 */

/** ISO weekday (1 = Monday) for a YYYY-MM-DD string. */
const isoDayOfWeek = (date) => {
  const parsed = new Date(`${date}T00:00:00`);
  return isNaN(parsed.getTime()) ? null : ((parsed.getDay() + 6) % 7) + 1;
};

const AddLessonModal = ({ campusId, campusName, defaultDate, rooms, prefill = {}, onClose, onSaved }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [form, setForm] = useState({
    title: '',
    guestName: '',
    date: prefill.date || defaultDate,
    startTime: prefill.startTime || '16:00',
    endTime: prefill.endTime || '17:00',
    room: prefill.room || '',
    lessonType: 'trial',
    status: 'pending',
    notes: ''
  });

  const setField = (key) => (event) =>
    setForm(prev => ({ ...prev, [key]: event.target.value }));

  // Picking Regular means "this runs every week", which is a course group, not a lesson.
  const asCourseGroup = form.lessonType === 'regular';

  const handleSave = async () => {
    if (!form.title.trim()) {
      setError('Give the lesson a name so it can be found later');
      return;
    }
    if (form.endTime <= form.startTime) {
      setError('End time must be after start time');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await addOneOffLesson({ campusId, ...form, title: form.title.trim() });
      await onSaved();
      onClose();
    } catch (e) {
      setError(e.message || 'Could not add the lesson');
      setSaving(false);
    }
  };

  if (asCourseGroup) {
    return (
      <CourseGroupFormModal
        mode="create"
        prefill={{
          campusId,
          dayOfWeek: isoDayOfWeek(form.date),
          startTime: form.startTime,
          endTime: form.endTime,
          room: form.room
        }}
        onSaved={onSaved}
        onClose={onClose}
      />
    );
  }

  return (
    <BaseModal maxWidth="580px">
      <ModalHeader>
        <h2>Add Lesson</h2>
        <CloseButton onClick={onClose} aria-label="Close">×</CloseButton>
      </ModalHeader>

      <ModalBody>
        {error && <ErrorState>{error}</ErrorState>}

        <Field>
          <label>Name *</label>
          <input
            type="text"
            value={form.title}
            onChange={setField('title')}
            placeholder="e.g. Trial – Emma Y5 Maths"
            autoFocus
          />
        </Field>

        <FieldRow>
          <Field>
            <label>Lesson type</label>
            <select value={form.lessonType} onChange={setField('lessonType')}>
              {LESSON_TYPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>{option.label}</option>
              ))}
            </select>
          </Field>
          <Field>
            <label>Status</label>
            <select value={form.status} onChange={setField('status')}>
              {LESSON_STATUS_OPTIONS
                .filter(option => !option.value.startsWith('no_show'))
                .map(option => (
                  <option key={option.value} value={option.value}>{option.label}</option>
                ))}
            </select>
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <label>Date</label>
            <input type="date" value={form.date} onChange={setField('date')} />
          </Field>
          <Field>
            <label>Room</label>
            <input
              type="text"
              value={form.room}
              onChange={setField('room')}
              placeholder="e.g. Room 3"
              list="timetable-room-options"
            />
            <datalist id="timetable-room-options">
              {(rooms || []).map(room => (
                <option key={room.key} value={room.name} />
              ))}
            </datalist>
          </Field>
        </FieldRow>

        <FieldRow>
          <Field>
            <label>Start time</label>
            <input type="time" value={form.startTime} onChange={setField('startTime')} />
          </Field>
          <Field>
            <label>End time</label>
            <input type="time" value={form.endTime} onChange={setField('endTime')} />
          </Field>
        </FieldRow>

        <Field>
          <label>Guest / student name</label>
          <input
            type="text"
            value={form.guestName}
            onChange={setField('guestName')}
            placeholder="For someone not yet enrolled"
          />
        </Field>

        <Field>
          <label>Notes</label>
          <textarea value={form.notes} onChange={setField('notes')} />
        </Field>

        <div style={{ fontSize: 12, color: '#6b7280' }}>
          Adding to <strong>{campusName}</strong>. This creates a standalone lesson and does
          not change any course group. For a class that runs every week, choose
          {' '}<strong>Regular</strong> above and the course group form opens instead.
        </div>
      </ModalBody>

      <ModalFooter>
        <SecondaryButton onClick={onClose} disabled={saving}>Cancel</SecondaryButton>
        <PrimaryButton onClick={handleSave} disabled={saving}>
          {saving ? 'Adding…' : 'Add Lesson'}
        </PrimaryButton>
      </ModalFooter>
    </BaseModal>
  );
};

export default AddLessonModal;
